import { claimDetailFooterSlot, provideDetailFooterSlot } from '@modules/common/components/detail-breadcrumbs/detail-footer-slot';
import { APP_CRUMBS, TREE_CRUMBS, UNTITLED_CRUMB, DetailNavigation } from '@modules/common/components/detail-breadcrumbs/detail-navigation';
import { type DetailCrumb } from '@modules/common/components/detail-breadcrumbs/detail-breadcrumbs.component';
import { UILocations } from '@modules/common/types/uiLocations';
import { canEditDocumentsIn } from '@modules/files/version-history/file-edit-authority';
import { ProjectVersionHistoryComponent } from '@modules/tasks/version-history/project-version-history.component';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { itemDisplayIdentity } from '@modules/common/components/item-workspace/item-display-identity';
import { ItemWorkspaceChipComponent } from '@modules/common/components/item-workspace/item-workspace-chip.component';
import { ItemWorkspaceAccessComponent } from '@modules/common/components/item-workspace/item-workspace-access.component';
import { ItemTeamLabelComponent } from '@modules/common/components/item-workspace/item-team-label.component';
import { ItemWorkspaceFooterComponent } from '@modules/common/components/item-workspace/item-workspace-footer.component';
import { itemChangeRevision } from '@modules/common/components/item-workspace/item-change-revision';
import { itemWorkspace } from '@modules/common/components/item-workspace/item-workspace-scope';
import { HttpContext } from '@angular/common/http';
import { ChangeDetectorRef, Component, computed, inject, Injector, input, OnInit, signal, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import type { Icon } from '@frontendlib/common/providers/icons/icons';
import { AlertService } from '@modules/alert/services/alert.service';
import { FullFormBaseComponent } from '@modules/common/components/full-form-base/full-form-base.component';
import {
  taskingDraftAudience,
  taskingDraftSuggestions,
  taskingWorkspaceIdentity,
} from '@modules/tasks/services/tasking-draft-audience';
import {
  MAILBOX_TENANT_ID,
  OWNING_SPACE_ID,
  owningTenantContext,
} from '@modules/common/interceptors/spaces.interceptor';
import { type StitchRequestConfig } from '@modules/common/services/base-stitch.service';
import { type ContainerDndConfig, ContainerDndService } from '@modules/common/services/container-dnd.service';
import { Tab } from '@modules/common/types/tab';
import { type DragData, DragDataTypes, isNotAlreadyLinked } from '@modules/drag-n-drop/types/drag-data';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { CalendarEventsService } from '@modules/form-controls/services/calendar-events.service';
import type { AutocompleteFactory } from '@frontendlib/common/types/autocomplete-factory';
import type { DropdownSelectItem } from '@frontendlib/common/types/dropdown-select-item';
import { priorities } from '@modules/form-controls/types/priorities';
import { BoardService } from '@modules/tasks/services/board.service';
import { ProjectsService } from '@modules/tasks/services/projects.service';
import { TaskingAppStateService } from '@modules/tasks/services/state.service';
import { TasksService } from '@modules/tasks/services/tasks.service';
import { TasksTenantScopeService } from '@modules/tasks/services/tasks-tenant-scope.service';
import { type WorkspaceIdentity, WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { Project, type ProjectFormGroupConfig } from '@modules/tasks/types/project';
import { TasksFilters } from '@modules/tasks/types/tasks-filters';
import { isBoardTabTaskDrop, PROJECT_BOARD_TAB } from '@modules/tasks/utils/project-form-dnd.util';
import { isOverdue, mergeDateAndTime } from '@sharedlib/common/utils/date';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { DragNDropService } from '@modules/drag-n-drop/services/drag-n-drop.service';
import { Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, tap, startWith, switchMap, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-project-form',
  templateUrl: './project-form.component.html',
  styleUrls: ['./project-form.component.less'],
  standalone: false,
  providers: [{ provide: CalendarEventsService, useClass: ProjectsService }, BoardService, WorkspaceIdentityService, provideDetailFooterSlot()],
})
export class ProjectFormComponent
  extends FullFormBaseComponent<StitchType.PROJECT, Project, ProjectFormGroupConfig>
  implements OnInit
{
  public readonly project = input(new Project());

  protected readonly UILocations = UILocations;
  private readonly restoredChangeDetector = inject(ChangeDetectorRef);

  public changesKey = 'project';
  public tabs: Tab[] = [
    { title: 'Tasks', value: 'project' },
    { title: 'Details', value: 'details' },
    { title: 'Attachments', value: 'attachments' },
    { title: 'Stitch', value: 'stitch' },
    { title: 'Comments', value: 'comments' },
  ];
  protected override attachmentsMenuStateVariant = 'projects';
  protected readonly viewMode = signal<'list' | 'board'>('board');
  /** main's view menu, back by Johanna's call (2026-10-21): the picked view with its icon. */
  protected readonly viewModeOptions: DropdownSelectItem<'list' | 'board'>[] = [
    { title: 'Board view', value: 'board', icon: 'sapphire-kanban-view' },
    { title: 'List view', value: 'list', icon: 'sapphire-row-vertical' },
  ];

  /** Only the Tasks tab (view menu) and the Stitch tab (its filters) fill the row above the content. */
  protected headingRowShown(): boolean {
    return this.selectedTab === 'project' || this.selectedTab === 'stitch';
  }
  public tasksFilters: TasksFilters;
  public parentSuggestions: AutocompleteFactory<Project>;
  public readonly priorityOptions: DropdownSelectItem<string>[] = priorities.map((p) => ({
    title: p.title,
    value: p.value,
    icon: p.icon,
  }));
  // Computed signal for selected priority icon
  public readonly selectedPriorityIcon = computed<Icon | undefined>(
    () => this.priorityOptions.find((p) => p.value === this.priorityControlValue())?.icon as Icon | undefined,
  );
  public readonly priorityControlValue = toSignal(
    this.formReady.pipe(
      switchMap((form) => {
        const priorityControl = form.get('priority');
        return priorityControl ? priorityControl.valueChanges.pipe(startWith(priorityControl.value)) : of(null);
      }),
      distinctUntilChanged(),
    ),
  );

  private readonly completed = signal<boolean>(false);

  private readonly toDateValue = toSignal(
    this.formReady.pipe(
      switchMap((form) => {
        const toDateControl = form.get('toDate');
        return toDateControl ? toDateControl.valueChanges.pipe(startWith(toDateControl.value)) : of(null);
      }),
    ),
  );

  private readonly toTimeValue = toSignal(
    this.formReady.pipe(
      switchMap((form) => {
        const toTimeControl = form.get('toTime');
        return toTimeControl ? toTimeControl.valueChanges.pipe(startWith(toTimeControl.value)) : of(null);
      }),
    ),
  );

  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = toObservable(this.displayTimeZoneService.timeZone);

  protected readonly isOverdue = computed<boolean>(() => {
    // See the same computed in `task-form.component.ts`. A project has no all-day flag, so a boundary is
    // floating exactly when it carries no time of day.
    const floating = !this.toTimeValue();
    return isOverdue({
      dueAt: mergeDateAndTime(this.toDateValue(), floating ? undefined : this.toTimeValue()),
      floating,
      completed: this.completed(),
      timeZone: this.displayTimeZoneService.timeZone(),
    });
  });

  protected dragDataType = DragDataTypes.project;
  protected override childItemDragType = DragDataTypes.task;
  protected override childItemContainerIdField = 'projectId';

  private readonly containerDndService = inject(ContainerDndService);
  private readonly alertService = inject(AlertService);
  private readonly boardService = inject(BoardService);
  private readonly dndService = inject(DragNDropService);
  private readonly tasksTenantScopeService = inject(TasksTenantScopeService, { optional: true });
  private readonly workspaceIdentityService = inject(WorkspaceIdentityService);
  private boardProjectId: string | undefined;
  protected readonly workspaceIdentity = signal<WorkspaceIdentity | undefined>(undefined);
  protected readonly displayWorkspaceIdentity = itemDisplayIdentity(
    this.workspaceIdentity,
    this.workspaceIdentityService,
    this.stitchItemChanged,
    () => this.stitchItem,
    () => this.dp,
  );

  protected readonly workspaceRevision = itemChangeRevision(this.stitchItemChanged);

  /** stitch-app-shell breadcrumbs, as on every detail page. */
  protected readonly navigation = new DetailNavigation(TaskingAppStateService, { appLabel: APP_CRUMBS.tasks });
  /** The item list's pager joins the breadcrumbs row in the main view. */
  private readonly footerSlot = claimDetailFooterSlot(this.navigation.enabled);

  /** [workspace ›] Tasking app › Projects › this project. */
  protected breadcrumbs(): DetailCrumb[] {
    this.workspaceRevision();
    return this.navigation.crumbs({
      workspace: itemWorkspace(this.displayWorkspaceIdentity(), this.stitchItem),
      view: TREE_CRUMBS.projects,
      fallbackView: TREE_CRUMBS.projects,
      itemTitle: this.stitchItem?.title || UNTITLED_CRUMB,
    });
  }
  protected readonly workspaceComponents = {
    'app-project-version-history': ProjectVersionHistoryComponent,
    'app-item-workspace-chip': ItemWorkspaceChipComponent,
    'app-item-workspace-access': ItemWorkspaceAccessComponent,
    'app-item-team-label': ItemTeamLabelComponent,
    'app-item-workspace-footer': ItemWorkspaceFooterComponent,
  };

  protected get insideTeam(): boolean {
    return !!itemWorkspace(this.displayWorkspaceIdentity(), this.stitchItem);
  }

  protected get workspaceLabelInputs(): Record<string, unknown> {
    return { identity: this.displayWorkspaceIdentity(), revision: this.workspaceRevision(), item: this.stitchItem };
  }

  protected readonly openWorkspaceAccess = (): void => {
    this.selectedTab = 'access';
  };

  protected get workspaceChipInputs(): Record<string, unknown> {
    return { ...this.workspaceLabelInputs, openAccess: this.openWorkspaceAccess, compact: this.contracted };
  }

  protected get workspaceTabs(): Tab[] {
    const tabs = this.tabsWithCounts();
    const versioned = this.historyAvailable() ? [...tabs, { title: 'Version history', value: 'versions' }] : tabs;
    return this.insideTeam && this.selectedTab === 'access' ? [...versioned, { title: 'Access', value: 'access' }] : versioned;
  }

  /** Saved projects offer history only while opened inside their owning team. */
  protected historyAvailable(): boolean {
    const project = this.project();
    return !!project.id && !!project.accountId &&
      this.spaceContextStore.type() === SpaceType.SHARED_SPACE &&
      project.accountId === this.spaceContextStore.contextId();
  }

  protected get versionHistoryInputs(): Record<string, unknown> {
    return {
      project: this.project(),
      canEdit: canEditDocumentsIn(this.permissionsService, this.project().accountId),
      held: this.saveInProgress || !this.pristine,
      restored: this.applyRestoredProject,
    };
  }

  protected readonly openVersionHistory = (): void => {
    if (this.historyAvailable()) this.selectedTab = 'versions';
  };

  /** The restore answer includes the current project's unchanged task counts and completion. */
  private readonly applyRestoredProject = (answer: Partial<Project>): void => {
    const fresh = new Project(answer);
    const project = this.project();
    if (fresh.id !== project.id || fresh.accountId !== project.accountId) return;
    const pristine = this.pristine;
    Object.assign(project, fresh);
    this.completed.set(!!project.completed);
    if (pristine) this.stitchItemChanged.next();
    this.itemUpdated.emit(project);
    this.restoredChangeDetector.markForCheck();
  };

  protected get workspaceFooterInputs(): Record<string, unknown> {
    return {
      identity: this.displayWorkspaceIdentity(),
      revision: this.workspaceRevision(),
      item: this.stitchItem,
      noun: 'Tasks',
      archived: this.form?.controls.archived.value ?? this.stitchItem.archived,
      deleted: this.form?.controls.deleted.value ?? this.stitchItem.deleted,
    };
  }

  protected readonly sharingTenantId = signal<string | null>(null);
  protected readonly settledTenant = toSignal(this.tasksTenantScopeService?.selectedTeamId() ?? of(null), {
    initialValue: undefined,
  });
  private readonly cancelPendingAudience = new Subject<void>();
  private awaitingDraftAudience = false;
  private creatingDraft: Project | undefined;
  protected readonly audienceResolved = computed(
    () => !!this.stitchItem?.accountId || !!this.requestContext || this.settledTenant() !== undefined,
  );
  protected readonly uploadTenantItems = computed(() => [
    { accountId: this.stitchItem?.accountId ?? this.sharingTenantId() ?? this.settledTenant() ?? undefined },
  ]);
  protected readonly saveLabel = computed(() => {
    const identity = this.workspaceIdentity();
    if (!identity) {
      return 'Save';
    }
    return this.sharingTenantId() === identity.spaceTenantId ? 'Save to team workspace' : 'Save to my workspace';
  });

  // Drives the panel-wide stitch drop border directly off the active drag rather than the
  // droppable's enter/leave, so it shows everywhere and doesn't flicker as the cursor passes
  // over child column/task zones. dndPredicate reads the live linkedInfo, so it also reflects
  // an unstitch immediately.
  protected readonly stitchDragActive = toSignal(
    this.dndService
      .getDraggingDataChanges()
      .pipe(
        map(
          (data) =>
            !!data &&
            this.dndService.getCurrentSpaceId() === this.spaceContextStore.contextId() &&
            this.dndPredicate(data),
        ),
      ),
    { initialValue: false },
  );

  /** Intercepts external tasks so invalid ones cannot fall through into a structural list move. */
  protected readonly incomingTaskPredicate = (data: DragData): boolean =>
    data.type === DragDataTypes.task && !isBoardTabTaskDrop(PROJECT_BOARD_TAB, data, this.stitchItem);

  private dndConfig(): ContainerDndConfig {
    return {
      containerType: DragDataTypes.project,
      childItemType: DragDataTypes.task,
      childService: this.tasksService,
      containerIdField: 'projectId',
      crossTenantCopySupported: true,
      cancelled: this.alive, isActive: () => !this.alive.isStopped,
      appStateService: this.stateService,
      spaceId: this.spaceContextStore.contextId(),
    };
  }

  constructor(
    private projectsService: ProjectsService,
    private tasksService: TasksService,
    private stateService: TaskingAppStateService,
    injector: Injector,
  ) {
    super(injector, projectsService);
    this.sharingTenantId.set(this.tasksTenantScopeService?.tenantId() ?? null);
    taskingWorkspaceIdentity(
      this.workspaceIdentityService,
      this.spaceContextStore,
      this.tasksTenantScopeService,
      () => {
        if (this.awaitingDraftAudience) {
          this.cancelPendingAudience.next();
          this.saveInProgress = false;
        }
        this.workspaceIdentity.set(undefined);
        this.sharingTenantId.set(this.stitchItem?.accountId ?? this.tasksTenantScopeService?.tenantId() ?? null);
      },
    )
      .pipe(takeUntilDestroyed())
      .subscribe((identity) => {
        this.workspaceIdentity.set(identity);
        if (!this.stitchItem?.id && identity) {
          this.sharingTenantId.set(
            this.stitchItem?.accountId ?? this.tasksTenantScopeService?.effectiveTenantId() ?? identity.memberTenantId,
          );
        }
      });
  }

  /**
   * Lifecycle
   */

  public override ngOnChanges(changes: SimpleChanges): void {
    if (changes.project && this.creatingDraft && this.stitchItem !== this.creatingDraft) {
      this.cancelPendingAudience.next();
      this.saveInProgress = false;
      this.creatingDraft = undefined;
      this.requestContext = this.stitchItem?.accountId ? owningTenantContext(this.stitchItem.accountId) : undefined;
      this.sharingTenantId.set(this.stitchItem?.accountId ?? this.settledTenant() ?? null);
    }
    super.ngOnChanges(changes);
    if (changes.tab && this.tab === 'versions') this.openVersionHistory();
  }

  private draftAudience(): Observable<HttpContext> {
    return taskingDraftAudience(
      this.stitchItem,
      this.requestContext,
      () => this.sharingTenantId(),
      this.tasksTenantScopeService,
    );
  }

  public override handleCancel(): void {
    if (this.awaitingDraftAudience) {
      this.cancelPendingAudience.next();
      this.saveInProgress = false;
    }
    super.handleCancel();
  }

  ngOnInit(): void {
    super.ngOnInit();

    const baseDndPredicate = this.dndPredicate;
    // Incoming tasks reach the project; its existing children keep their structural board routing.
    this.dndPredicate = (dragData: DragData): boolean =>
      dragData.type === DragDataTypes.task
        ? !isBoardTabTaskDrop(PROJECT_BOARD_TAB, dragData, this.stitchItem) && isNotAlreadyLinked(dragData, this.linkedInfo())
        : baseDndPredicate(dragData);

    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => this.showProjectOnBoard());
    this.itemUpdated
      .pipe(
        filter(() => !!this.stitchItem?.id && this.stitchItem.id !== this.boardProjectId),
        takeUntil(this.alive),
      )
      .subscribe(() => this.showProjectOnBoard());

    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => this.completed.set(this.stitchItem.completed));
    this.itemUpdated.pipe(takeUntil(this.alive)).subscribe(() => this.completed.set(this.stitchItem.completed));
    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      if (this.stitchItem?.accountId) {
        this.sharingTenantId.set(this.stitchItem.accountId);
        this.requestContext = owningTenantContext(this.stitchItem.accountId);
      }
    });

    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      this.parentSuggestions = taskingDraftSuggestions(
        () => this.draftAudience(),
        (context) =>
          this.projectsService.getAutocompleteSuggestions({ exceptIds: [this.stitchItem.id] }, () => context),
      );
    });

    // Menu removed, so persisted state must not apply; containersIds is format()-only, the binding a kept seam.
    this.stitchItemChanged.pipe(takeUntil(this.alive)).subscribe(() => {
      this.tasksFilters = new TasksFilters({ containersIds: [this.stitchItem.id] });
    });
  }

  /**
   * Actions
   */

  dndDrop(dragData: DragData): void {
    if (!this.stitchItem.id || !this.dndPredicate(dragData)) return;
    this.containerDndService.handleDndDrop(
      this.stitchItem,
      dragData,
      this.dndConfig(),
      this.stitchService,
      this.alertService,
    );
  }

  handleComplete(): void {
    this.stitchItem.completed = !this.stitchItem.completed;
    this.completed.set(this.stitchItem.completed);
    this.projectsService.update(this.stitchItem);
  }

  /**
   * Helpers
   */

  get stitchItem(): Project {
    return this.project();
  }

  protected shouldRefreshList(prev, current) {
    return Project.shouldRefreshList(prev, current);
  }

  protected fromFormGroup(): Project {
    return Project.fromFormGroup(this.form, this.displayTimeZoneService.timeZone());
  }

  protected override performCreate(stitchItem: Project): Observable<Project> {
    this.creatingDraft = this.stitchItem;
    this.awaitingDraftAudience = true;
    return taskingDraftAudience(
      this.stitchItem,
      this.requestContext,
      () => this.sharingTenantId(),
      this.tasksTenantScopeService,
    ).pipe(
      tap((context) => {
        this.awaitingDraftAudience = false;
        this.requestContext = context;
        const owner = context.get(OWNING_SPACE_ID) ?? context.get(MAILBOX_TENANT_ID);
        if (owner) {
          this.stitchItem.accountId = owner;
          stitchItem.accountId = owner;
          this.sharingTenantId.set(owner);
        }
      }),
      switchMap(() => super.performCreate(stitchItem)),
      takeUntil(this.cancelPendingAudience),
      takeUntil(this.alive),
      finalize(() => {
        this.awaitingDraftAudience = false;
        this.saveInProgress = false;
      }),
    );
  }

  protected override requestConfig(): StitchRequestConfig {
    const requestContext = this.itemRequestContext();
    if (!requestContext) {
      return super.requestConfig();
    }
    return { requestContext };
  }

  private showProjectOnBoard(): void {
    this.boardProjectId = this.stitchItem?.id;
    this.boardService.setProject(this.stitchItem);
  }

  private itemRequestContext(): HttpContext | undefined {
    return owningTenantContext(this.stitchItem?.accountId ?? this.sharingTenantId()) ?? this.requestContext;
  }

  protected asFormGroup() {
    return this.project().asFormGroup({
      projectsService: this.projectsService,
      maxAttachmentSize: this.maxUploadSize(),
      timeZone: this.displayTimeZoneService.timeZone(),
    });
  }
}
